import { AnimatePresence } from "motion/react";
import type React from "react";

import { SidebarItemFloatingTooltip } from "@/components/Sidebar/SidebarItem";
import { useSidebarCollapsedTooltip } from "@/components/Sidebar/useSidebarCollapsedTooltip";
import { Button } from "@/design-system/Button";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import { RuntimeFloatingPortal } from "@/ui-adapters/RuntimeFloatingPanel";

/**
 * Sidebar entry that opens the Ask Nobi chat, styled as an AI button. It follows the
 * sidebar's collapsed state on its own: collapsed it shrinks to the icon and shows
 * the label in a floating tooltip instead.
 */
export function AskNobiNavigationItem(): React.ReactNode {
  const { t } = useTranslation();
  const { showTooltip, hideTooltip, referenceRef, panelProps, isTooltipVisible, isCollapsed } =
    useSidebarCollapsedTooltip();

  return (
    <>
      <div
        className={isCollapsed ? "size-control-md" : "w-full"}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        ref={referenceRef}
      >
        <Button
          type="link"
          href={routes.askNobiChat.chat()}
          variant="ai"
          size={isCollapsed ? "md" : "lg"}
          fullWidth
          aria-label={t("page.ask-nobi-chat.header.title")}
        >
          {!isCollapsed && (
            <span className="-ml-space-xs min-w-0 truncate">{t("page.ask-nobi-chat.sidebar-item")}</span>
          )}
        </Button>
      </div>
      <AnimatePresence>
        {isCollapsed && isTooltipVisible && (
          <RuntimeFloatingPortal>
            <SidebarItemFloatingTooltip
              label={t("page.ask-nobi-chat.header.title")}
              floating={panelProps}
              isVisible={isTooltipVisible}
            />
          </RuntimeFloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
}
